<script>
import { mapState } from "vuex";
import { api } from "@/state/services";
import StKpiCard from "@/components/base/st-kpi-card";
import MetasSelosHistorico from "@/components/metas/metas-selos-historico";
import AvisoTruck from "@/components/metas/aviso-truck";
import {
  carregarConfigMetas,
  produtosVisiveis,
  isSecretaria,
  temApvs,
  mesAno,
  nomeMes,
  plural,
  fmtData,
  classeStatus,
  textoPrazo,
} from "@/utils/minhas-metas";
import {
  carregarJornada,
  mesclarJornada,
  fluxoGlobalBloqueado,
  tourConcluido,
  hojeBrt,
  lerVistaLocal,
  gravarVistaLocal,
  animarNumero,
  prefereMenosMovimento,
} from "@/utils/jornada";

const LIMITE_LISTA = 30;
const FASES = [
  { key: "abertura", titulo: "Vamos começar", icone: "bx bx-flag" },
  { key: "adesoes", titulo: "Adesões do mês", icone: "bx bx-plus-circle" },
  { key: "fechamento", titulo: "Fechamento", icone: "bx bx-target-lock" },
  { key: "contam", titulo: "Contam no fechamento", icone: "bx bx-list-check" },
  { key: "final", titulo: "Conquista", icone: "bx bx-trophy" },
];
// Revelação da barra do fechamento: 0 = só o preenchimento; 1..3 = marcas; 4 = histórico + selos.
const SUB_FECHAMENTO_MAX = 4;
// Só o que ainda dá para garantir neste fechamento. "estourado" (passou dos 30 dias) já não conta:
// vira apenas um contador de perdidas, para não diluir a lista de ação.
const ORDEM_ATENCAO = { pendente: 0, pago_aguardando: 1 };

/**
 * JORNADA DIÁRIA — Minhas Metas em fases, na 1ª abertura do app no dia (dia de America/Sao_Paulo).
 *
 * PRECEDÊNCIA (montado por ÚLTIMO em router/layouts/main.vue):
 *   RegionalCpfModal -> AfiliadosRegulamentoModal -> OnboardingModal (tour) -> MetasOnboardingModal
 *   -> ESTE. Só abre sozinho quando: nenhum fluxo global em cena (utils/jornada::fluxoGlobalBloqueado,
 *   mesmos bloqueios do tour/wizard + wizard de metas aberto), tour concluído, usuário com APVS,
 *   não é secretária, e o servidor responde deve_exibir=true (jornada ativa + planejamento
 *   concluído + ainda não vista hoje). "Rever jornada agora" (Minhas Metas) abre manualmente.
 *
 * Ao concluir, pular ou fechar: POST metas/jornada/vista (idempotente) + cache local do dia.
 * Regras (janelas, status, marcas) vêm prontas do GET metas/painel — nada é recalculado aqui.
 *
 * b-modal controlado por :visible/@change (NÃO v-model/:value).
 */
export default {
  name: "JornadaDiariaModal",
  components: { StKpiCard, MetasSelosHistorico, AvisoTruck },
  data() {
    return {
      FASES,
      SUB_MAX: SUB_FECHAMENTO_MAX,
      aberto: false,
      preparando: false,
      fase: 0,
      sub: 0,
      produto: null,
      produtos: [],
      paineis: {},
      config: null,
      marcada: false,
      sequenciaDias: 0,
      vistaLocal: null,
      reduzido: false,
      n: { feitas: 0, porDia: 0, contandoBarra: 0, contando: 0, pendentes: 0, faltam: 0, total: 0 },
      cancelarAnim: [],
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    user() {
      return (this.account && this.account.user) || null;
    },
    bloqueado() {
      return fluxoGlobalBloqueado(this.account, this.$route);
    },
    podeUsar() {
      return !!this.user && !isSecretaria(this.user) && temApvs(this.user) && !this.bloqueado;
    },
    elegivelAuto() {
      return this.podeUsar && tourConcluido(this.user) && this.vistaLocal !== hojeBrt();
    },
    pedidoManual() {
      return !!(this.account && this.account.jornadaReplay);
    },
    painel() {
      return this.produto ? this.paineis[this.produto] || null : null;
    },
    p1() {
      return this.painel ? this.painel.passo1 : null;
    },
    p2() {
      return this.painel ? this.painel.passo2 : null;
    },
    regras() {
      return this.painel ? this.painel.regras : null;
    },
    gami() {
      return this.painel ? this.painel.gamificacao : null;
    },
    isRegional() {
      return !!(this.painel && this.painel.perfil && this.painel.perfil.tipo === "regional");
    },
    nomeProduto() {
      return this.produto === "truck" ? "APVS Truck" : "APVS Brasil";
    },
    primeiroNome() {
      const nome = (this.user && this.user.name) || "";
      const p = nome.trim().split(" ")[0] || "";
      return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : "";
    },
    faseAtual() {
      return FASES[this.fase];
    },
    ultimaFase() {
      return this.fase === FASES.length - 1;
    },
    rotuloFechamento() {
      return this.p1 ? mesAno(this.p1.fechamento.mes, this.p1.fechamento.ano) : "";
    },
    nomeMesFechamento() {
      return this.p1 ? nomeMes(this.p1.fechamento.mes).toLowerCase() : "";
    },
    tituloFase() {
      const k = this.faseAtual.key;
      if (k === "adesoes" && this.p2) return "Adesões de " + nomeMes(this.p2.mes);
      if (k === "fechamento") return "Fechamento de " + this.rotuloFechamento;
      if (k === "contam") return "Contam no Fechamento de " + this.rotuloFechamento;
      return this.faseAtual.titulo;
    },
    progresso() {
      // Fase 2 conta as sub-etapas para a barra andar a cada "Próximo".
      const total = FASES.length - 1;
      let pos = this.fase;
      if (this.faseAtual.key === "fechamento") pos += this.sub / (SUB_FECHAMENTO_MAX + 1);
      return Math.round((pos / total) * 100);
    },
    mostrarProdutos() {
      return this.produtos.length > 1 && ["adesoes", "fechamento", "contam"].indexOf(this.faseAtual.key) !== -1;
    },

    // ---- Fechamento: barra com as 3 marcas
    marcas() {
      if (!this.regras) return [];
      const pessoal = this.regras.meta_pessoal_efetiva;
      return [
        { n: 1, valor: this.regras.meta_oficial, label: "Meta", classe: "jd-m1" },
        { n: 2, valor: this.regras.recomendado, label: "Gordura", classe: "jd-m2" },
        { n: 3, valor: pessoal, label: "Sua meta", classe: "jd-m3" },
      ];
    },
    escala() {
      const vals = this.marcas.map((m) => Number(m.valor) || 0);
      vals.push(this.p1 ? this.p1.resumo.contando : 0);
      return Math.max(1, ...vals);
    },
    larguraBarra() {
      return Math.min(100, (this.n.contandoBarra * 100) / this.escala) + "%";
    },
    varianteBarra() {
      if (!this.regras || !this.p1) return "danger";
      const c = this.p1.resumo.contando;
      if (c >= this.regras.meta_oficial) return "success";
      if (c * 2 >= this.regras.meta_oficial) return "warning";
      return "danger";
    },
    explicacaoSub() {
      if (!this.regras || !this.p1) return null;
      const c = this.p1.resumo.contando;
      const r = this.regras;
      if (this.sub === 0) {
        return {
          titulo: c + " " + plural(c, "adesão já está contando", "adesões já estão contando"),
          texto: "Esta barra mostra quantas adesões já contam para o fechamento de " + this.rotuloFechamento +
            ". Agora vamos ver o que cada marcação significa.",
        };
      }
      if (this.sub === 1) {
        return {
          titulo: "1ª marcação — o mínimo: " + r.meta_oficial,
          texto: "É a meta oficial. Com " + r.meta_oficial + " adesões contando você bate a meta do fechamento." +
            (c >= r.meta_oficial ? " Você já passou dela!" : " Faltam " + (r.meta_oficial - c) + "."),
        };
      }
      if (this.sub === 2) {
        return {
          titulo: "2ª marcação — a gordura: " + r.recomendado,
          texto: "É uma folga para imprevistos: se algum associado não pagar o 1º boleto dentro do prazo, " +
            "a gordura garante que você continue batendo a meta.",
        };
      }
      if (this.sub === 3) {
        return {
          titulo: "3ª marcação — a sua meta: " + r.meta_pessoal_efetiva,
          texto: r.meta_pessoal == null
            ? "Você ainda não definiu a sua meta pessoal, então usamos o mínimo recomendado. Dá para ajustar em Minhas Metas."
            : "É a meta que você escolheu no seu planejamento. É ela que mostra até onde você quer chegar.",
        };
      }
      return {
        titulo: "Seus meses anteriores e o atual",
        texto: "Aqui ficam os seus últimos fechamentos: verde é mês com a meta batida. Na página Minhas Metas você " +
          "sempre encontra as metas dos meses anteriores e a do mês atual.",
      };
    },

    // ---- Contam no fechamento: só o que exige atenção (nem contando, nem cancelada)
    itensAtencao() {
      if (!this.p1) return [];
      return this.p1.itens
        .filter((i) => i.status && ORDEM_ATENCAO[i.status.key] !== undefined)
        .slice()
        .sort((a, b) => {
          const oa = ORDEM_ATENCAO[a.status.key];
          const ob = ORDEM_ATENCAO[b.status.key];
          if (oa !== ob) return oa - ob;
          const aa = a.status.alerta ? 0 : 1;
          const ab = b.status.alerta ? 0 : 1;
          if (aa !== ab) return aa - ab;
          return (a.status.dias_prazo != null ? a.status.dias_prazo : 9999) - (b.status.dias_prazo != null ? b.status.dias_prazo : 9999);
        });
    },
    itensVisiveis() {
      return this.itensAtencao.slice(0, LIMITE_LISTA);
    },
    corte() {
      return this.p1 ? this.p1.corte : null;
    },
    // Marcas com valores próximos: a 3ª desce para baixo da barra quando colide com a 1ª (a 2ª já
    // fica embaixo); se colidir com a 2ª, fica em cima — evita círculos sobrepostos.
    marcasPos() {
      const perto = (a, b) => Math.abs(Number(a) - Number(b)) / this.escala < 0.08;
      return this.marcas.map((m) => {
        let baixo = m.n === 2;
        if (m.n === 3) {
          baixo = perto(m.valor, this.marcas[0].valor) && !perto(m.valor, this.marcas[1].valor);
        }
        return { ...m, baixo };
      });
    },
    nivelTexto() {
      return this.gami ? this.gami.nivel.label : "";
    },
  },
  watch: {
    elegivelAuto: {
      immediate: true,
      handler(val) {
        if (val) this.verificar();
      },
    },
    pedidoManual: {
      immediate: true,
      handler(val) {
        if (!val) return;
        if (!this.podeUsar) {
          this.$store.commit("account/setJornadaReplay", false);
          return;
        }
        this.preparar(true);
      },
    },
    bloqueado(val) {
      // Um fluxo obrigatório entrou em cena: sai sem marcar como vista (volta depois).
      if (val && this.aberto) this.aberto = false;
    },
    fase() {
      this.aoEntrarFase();
    },
    produto() {
      if (this.aberto) this.aoEntrarFase();
    },
  },
  created() {
    this.reduzido = prefereMenosMovimento();
    this.vistaLocal = lerVistaLocal(this.user);
  },
  mounted() {
    document.addEventListener("visibilitychange", this.aoVoltarAoApp);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.aoVoltarAoApp);
    // Saiu da tela com a jornada aberta (troca de rota, "voltar" do Android): conta como fechada,
    // para não recomeçar da fase 1 na próxima tela.
    if (this.aberto) this.marcarVista();
    this.pararAnimacoes();
  },
  methods: {
    nomeMes,
    plural,
    fmtData,
    classeStatus,
    textoPrazo,
    fmtNum(v, casas) {
      return Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: casas || 0, minimumFractionDigits: 0 });
    },
    // App (webview) volta do segundo plano num dia novo sem trocar de rota: reavalia.
    aoVoltarAoApp() {
      if (document.visibilityState !== "visible" || this.aberto) return;
      this.vistaLocal = lerVistaLocal(this.user);
      if (this.elegivelAuto) this.verificar();
    },
    verificar() {
      if (this.preparando || this.aberto) return;
      carregarJornada(this.$store)
        .then((estado) => {
          if (!estado || !estado.disponivel) return;
          if (estado.vista_hoje) {
            gravarVistaLocal(this.user, estado.hoje);
            this.vistaLocal = estado.hoje;
            return;
          }
          if (estado.deve_exibir && this.elegivelAuto) this.preparar(false);
        })
        .catch(() => {
          // Silencioso: a jornada é opcional.
        });
    },
    preparar(manual) {
      if (this.preparando || this.aberto) return;
      this.preparando = true;
      carregarConfigMetas(this.$store)
        .then((cfg) => {
          this.config = cfg;
          const lista = produtosVisiveis(cfg);
          return Promise.all(
            lista.map((p) =>
              api
                .get("metas/painel?produto=" + encodeURIComponent(p))
                .then((r) => (r && r.data && r.data.status === "success" ? [p, r.data] : null))
                .catch(() => null)
            )
          );
        })
        .then((res) => {
          const paineis = {};
          const produtos = [];
          (res || []).forEach((x) => {
            if (x) {
              paineis[x[0]] = x[1];
              produtos.push(x[0]);
            }
          });
          // Revalida: algum fluxo obrigatório pode ter entrado enquanto carregava.
          if (!produtos.length || !this.podeUsar || (!manual && !this.elegivelAuto)) return;
          this.paineis = paineis;
          this.produtos = produtos;
          this.produto = produtos[0];
          this.fase = 0;
          this.sub = 0;
          this.marcada = false;
          const estado = this.account.jornadaEstado && this.account.jornadaEstado.data;
          this.sequenciaDias = estado && estado.sequencia ? estado.sequencia : 0;
          this.aberto = true;
          this.$nextTick(this.aoEntrarFase);
        })
        .catch(() => {})
        .finally(() => {
          this.preparando = false;
          if (manual) this.$store.commit("account/setJornadaReplay", false);
        });
    },

    // ---- animações
    pararAnimacoes() {
      this.cancelarAnim.forEach((c) => c());
      this.cancelarAnim = [];
    },
    animar(campo, alvo, duracao) {
      this.n[campo] = 0;
      this.cancelarAnim.push(
        animarNumero(0, alvo, duracao || 900, (v) => {
          this.n[campo] = v;
        })
      );
    },
    aoEntrarFase() {
      if (!this.painel) return;
      this.pararAnimacoes();
      const k = this.faseAtual.key;
      if (k === "adesoes") {
        this.animar("feitas", this.p2.feitas);
        this.animar("porDia", this.p2.faltam ? this.p2.por_dia : 0);
      } else if (k === "fechamento") {
        this.animar("contandoBarra", this.p1.resumo.contando, 1100);
      } else if (k === "contam") {
        this.animar("total", this.p1.resumo.total);
        this.animar("contando", this.p1.resumo.contando);
        this.animar("pendentes", this.p1.resumo.pendentes);
        this.animar("faltam", this.p1.resumo.faltam);
      } else if (k === "final") {
        this.marcarVista();
      }
      this.$nextTick(() => {
        const alvo = this.$refs.tituloFase;
        if (alvo && alvo.focus) alvo.focus();
      });
    },

    // ---- navegação
    trocarProduto(p) {
      if (p === this.produto) return;
      this.produto = p;
      if (this.faseAtual.key === "fechamento") this.sub = 0;
    },
    proximo() {
      if (this.faseAtual.key === "fechamento" && this.sub < SUB_FECHAMENTO_MAX) {
        this.sub++;
        return;
      }
      if (this.ultimaFase) {
        this.fechar();
        return;
      }
      this.fase++;
      this.sub = 0;
    },
    voltar() {
      if (this.faseAtual.key === "fechamento" && this.sub > 0) {
        this.sub--;
        return;
      }
      if (this.fase > 0) {
        this.fase--;
        this.sub = this.faseAtual.key === "fechamento" ? SUB_FECHAMENTO_MAX : 0;
      }
    },
    pular() {
      this.fechar();
    },
    verMinhasMetas() {
      this.fechar();
      if (this.$route.path !== "/minhas-metas") {
        this.$router.push("/minhas-metas").catch(() => {});
      }
    },
    marcarVista() {
      if (this.marcada) return;
      this.marcada = true;
      const hoje = hojeBrt();
      gravarVistaLocal(this.user, hoje);
      this.vistaLocal = hoje;
      api
        .post("metas/jornada/vista", {})
        .then((response) => {
          const d = response && response.data;
          if (d && d.status === "success") {
            this.sequenciaDias = d.sequencia || this.sequenciaDias;
            if (d.hoje) gravarVistaLocal(this.user, d.hoje);
            mesclarJornada(this.$store, { vista_hoje: true, deve_exibir: false, sequencia: d.sequencia });
          }
        })
        .catch(() => {
          // Sem conexão: o cache local evita reabrir hoje neste aparelho.
        });
    },
    fechar() {
      if (!this.aberto) return;
      this.marcarVista();
      this.pararAnimacoes();
      this.aberto = false;
    },
    onChange(visivel) {
      if (!visivel) this.fechar();
    },
  },
};
</script>

<template>
  <b-modal
    :visible="aberto"
    size="lg"
    centered
    scrollable
    no-close-on-backdrop
    dialog-class="jd-dialog"
    content-class="jd-content"
    header-class="jd-header"
    body-class="jd-body"
    footer-class="jd-footer"
    title-tag="h2"
    @change="onChange"
  >
    <template #modal-title>
      <span class="jd-kicker">Jornada diária · {{ fase + 1 }} de {{ FASES.length }}</span>
      <span ref="tituloFase" class="jd-title" tabindex="-1">{{ tituloFase }}</span>
    </template>

    <div v-if="painel" class="jd" :class="{ 'jd-reduzido': reduzido }">
      <!-- Trilha de fases (mapa) -->
      <div class="jd-trilha" aria-hidden="true">
        <div class="jd-trilha-linha"><div class="jd-trilha-fill" :style="{ width: progresso + '%' }"></div></div>
        <ol class="jd-trilha-nos">
          <li v-for="(f, i) in FASES" :key="f.key" :class="{ feito: i < fase, ativo: i === fase }">
            <i :class="i < fase ? 'bx bx-check' : f.icone"></i>
          </li>
        </ol>
      </div>
      <div class="sr-only" role="status" aria-live="polite">Fase {{ fase + 1 }} de {{ FASES.length }}: {{ tituloFase }}</div>

      <!-- Produto -->
      <div v-if="mostrarProdutos" class="jd-tabs" role="group" aria-label="Produto">
        <button
          v-for="p in produtos"
          :key="p"
          type="button"
          class="jd-tab"
          :class="{ ativo: p === produto }"
          :aria-pressed="p === produto ? 'true' : 'false'"
          @click="trocarProduto(p)"
        >
          {{ p === "truck" ? "APVS Truck" : "APVS Brasil" }}
        </button>
      </div>

      <AvisoTruck v-if="produto === 'truck' && faseAtual.key !== 'abertura'" compacto />

      <transition name="jd-fase" mode="out-in">
        <!-- 0. Abertura -->
        <section v-if="faseAtual.key === 'abertura'" key="abertura" class="jd-fase jd-abertura">
          <div class="jd-hero-icone" aria-hidden="true"><i class="bx bx-map-alt"></i></div>
          <p class="jd-saudacao">Olá{{ primeiroNome ? ", " + primeiroNome : "" }}!</p>
          <h3 class="jd-hero-titulo">Vamos acompanhar suas metas pessoais</h3>
          <p class="jd-lead">
            Em poucos passos você vê como está o seu mês, o que já conta para o fechamento de
            <strong>{{ rotuloFechamento }}</strong> e quais adesões precisam da sua atenção hoje.
          </p>
          <ol class="jd-mapa">
            <li v-for="(f, i) in FASES.slice(1)" :key="f.key" :style="{ animationDelay: i * 120 + 'ms' }">
              <span class="jd-mapa-no"><i :class="f.icone" aria-hidden="true"></i></span>
              <span>{{ f.titulo }}</span>
            </li>
          </ol>
          <p v-if="sequenciaDias > 1" class="jd-streak">
            <i class="bx bxs-flame" aria-hidden="true"></i> {{ sequenciaDias }} dias seguidos acompanhando
          </p>
        </section>

        <!-- 1. Adesões do mês (meta pessoal) -->
        <section v-else-if="faseAtual.key === 'adesoes'" :key="'adesoes-' + produto" class="jd-fase">
          <p class="jd-lead">
            Estas são as adesões que você fez em <strong>{{ nomeMes(p2.mes) }}</strong> no {{ nomeProduto }},
            comparadas com a sua meta pessoal.
          </p>
          <div class="jd-kpis">
            <StKpiCard label="Feitas no mês" :value="fmtNum(n.feitas)" icon="bx bx-plus-circle" variant="brand" />
            <StKpiCard label="Meta pessoal" :value="p2.meta_pessoal" icon="bx bx-target-lock" variant="info"
              :hint="regras.meta_pessoal == null ? 'mínimo recomendado' : 'definida por você'" />
            <StKpiCard label="Dias restantes" :value="p2.dias_restantes" icon="bx bx-calendar" variant="neutral" hint="contando hoje" />
            <StKpiCard label="Por dia" :value="fmtNum(n.porDia, 1)" icon="bx bx-trending-up"
              :variant="p2.faltam ? 'warning' : 'success'"
              :hint="p2.faltam ? 'para chegar em ' + p2.meta_pessoal + ' (faltam ' + p2.faltam + ')' : 'meta pessoal atingida'" />
          </div>
          <div class="jd-barra-simples" role="progressbar" :aria-valuenow="p2.feitas" aria-valuemin="0" :aria-valuemax="Math.max(p2.meta_pessoal, p2.feitas)"
            :aria-valuetext="p2.feitas + ' de ' + p2.meta_pessoal">
            <div class="jd-barra-simples-fill" :class="p2.feitas >= p2.meta_pessoal ? 'ok' : 'andando'"
              :style="{ width: Math.min(100, p2.meta_pessoal ? (n.feitas * 100) / p2.meta_pessoal : 0) + '%' }"></div>
          </div>
          <p class="jd-legenda">{{ fmtNum(n.feitas) }} de {{ p2.meta_pessoal }} na sua meta pessoal de {{ nomeMes(p2.mes) }}</p>
          <p class="jd-dica">
            <i class="bx bx-info-circle" aria-hidden="true"></i>
            <span>
              Estas adesões contam no fechamento dos <strong>2 meses seguintes</strong>, desde que o 1º boleto seja pago
              até o dia {{ regras.dia_corte }} do mês do fechamento e sem passar de {{ regras.dias_apos_vencimento }} dias do vencimento.
            </span>
          </p>
        </section>

        <!-- 2. Fechamento: barra revelada aos poucos -->
        <section v-else-if="faseAtual.key === 'fechamento'" :key="'fechamento-' + produto" class="jd-fase">
          <div class="jd-fech-top">
            <p class="jd-muted mb-0">{{ nomeProduto }} · {{ isRegional ? "contagem da regional (equipe + você)" : "sua produção própria" }}</p>
            <transition name="jd-pop">
              <span v-if="sub >= SUB_MAX" class="jd-nivel" :class="'jd-nivel-' + gami.nivel.key">
                <i class="bx bx-medal" aria-hidden="true"></i> {{ gami.nivel.label }}
              </span>
            </transition>
          </div>

          <div class="jd-barra-wrap">
            <div class="jd-barra-valor"><strong>{{ fmtNum(n.contandoBarra) }}</strong> contando</div>
            <div class="jd-barra" role="progressbar" :aria-valuenow="p1.resumo.contando" aria-valuemin="0" :aria-valuemax="escala"
              :aria-valuetext="p1.resumo.contando + ' contando; meta ' + regras.meta_oficial">
              <div class="jd-barra-fill" :class="'jd-fill-' + varianteBarra" :style="{ width: larguraBarra }"></div>
              <transition-group name="jd-marca" tag="div" class="jd-marcas">
                <span
                  v-for="m in marcasPos.filter((x) => sub >= x.n)"
                  :key="m.n"
                  class="jd-marca"
                  :class="[m.classe, { atual: sub === m.n, ok: p1.resumo.contando >= m.valor, baixo: m.baixo }]"
                  :style="{ left: Math.min(100, (m.valor * 100) / escala) + '%' }"
                >
                  <span class="jd-marca-num">{{ m.n }}</span>
                </span>
              </transition-group>
            </div>
            <transition-group name="jd-marca" tag="ul" class="jd-legenda-marcas">
              <li v-for="m in marcas.filter((x) => sub >= x.n)" :key="'l' + m.n" :class="[m.classe, { atual: sub === m.n }]">
                <span class="jd-dot">{{ m.n }}</span> {{ m.label }}: <strong>{{ m.valor }}</strong>
              </li>
            </transition-group>
          </div>

          <transition name="jd-fase" mode="out-in">
            <div v-if="explicacaoSub" :key="'exp' + sub" class="jd-explica" aria-live="polite">
              <strong>{{ explicacaoSub.titulo }}</strong>
              <span>{{ explicacaoSub.texto }}</span>
            </div>
          </transition>

          <MetasSelosHistorico
            v-if="sub >= SUB_MAX"
            :gamificacao="gami"
            revelar
            :legenda="isRegional ? 'Contagem da regional (equipe + você).' : 'Contagem da sua produção própria.'"
          />
        </section>

        <!-- 3. Contam no fechamento: só o que exige atenção -->
        <section v-else-if="faseAtual.key === 'contam'" :key="'contam-' + produto" class="jd-fase">
          <p class="jd-lead">
            Agora, vamos mostrar a você as suas adesões que você precisa acompanhar para garantir a ativação e
            contabilização para a sua meta do fechamento de <strong>{{ nomeMesFechamento }}</strong>.
          </p>
          <div class="jd-kpis">
            <StKpiCard label="Total" :value="fmtNum(n.total)" icon="bx bx-list-ul" variant="brand" />
            <StKpiCard label="Já contando" :value="fmtNum(n.contando)" icon="bx bx-check-circle" variant="success"
              :hint="p1.resumo.percentual + '% da meta'" />
            <StKpiCard label="Pendentes" :value="fmtNum(n.pendentes)" icon="bx bx-time" variant="warning"
              :hint="p1.resumo.alertas ? p1.resumo.alertas + ' com prazo curto' : 'aguardando o 1º boleto'" />
            <StKpiCard label="Falta para a meta" :value="fmtNum(n.faltam)" icon="bx bx-target-lock"
              :variant="p1.resumo.faltam === 0 ? 'success' : 'danger'" />
          </div>

          <div
            v-if="corte"
            class="jd-corte"
            :class="{ urgente: corte.periodo_corte || corte.dias_restantes <= regras.alerta_dias }"
            role="note"
          >
            <i class="bx bx-calendar-event" aria-hidden="true"></i>
            <span>
              Corte em <strong>{{ fmtData(corte.data) }}</strong>
              <template v-if="corte.dias_restantes > 0"> · faltam {{ corte.dias_restantes }} {{ plural(corte.dias_restantes, "dia", "dias") }}</template>
              <template v-else-if="corte.dias_restantes === 0"> · é hoje</template>
              <template v-if="!corte.dia_util"> · oriente o pagamento até <strong>{{ fmtData(corte.pagar_ate) }}</strong> ({{ corte.motivo }})</template>
            </span>
          </div>

          <div v-if="!itensAtencao.length" class="jd-vazio">
            <div class="jd-vazio-icone" aria-hidden="true"><i class="bx bx-party"></i></div>
            <strong>Nenhuma adesão pendente!</strong>
            <span>Não há adesão aguardando pagamento para o fechamento de {{ rotuloFechamento }}. Continue assim.</span>
          </div>
          <template v-else>
            <p class="jd-muted">
              {{ itensAtencao.length }} {{ plural(itensAtencao.length, "adesão precisa", "adesões precisam") }} da sua atenção:
            </p>
            <transition-group name="jd-item" tag="ul" class="jd-lista" appear>
              <li
                v-for="(item, i) in itensVisiveis"
                :key="(item.placa || '') + '-' + i"
                class="jd-item"
                :class="{ alerta: item.status.alerta, perdida: item.status.key === 'estourado' }"
                :style="{ transitionDelay: Math.min(i, 10) * 40 + 'ms' }"
              >
                <div class="jd-item-top">
                  <strong class="jd-item-placa">{{ item.placa || "Sem placa" }}</strong>
                  <span class="mm-badge" :class="classeStatus(item.status.key)">{{ item.status.label }}</span>
                </div>
                <div class="jd-item-nome">{{ item.associado }}</div>
                <div v-if="item.consultor" class="jd-item-sub">Consultor: {{ item.consultor }}</div>
                <div class="jd-item-sub" :class="{ 'jd-prazo-alerta': item.status.alerta || item.status.key === 'estourado' }">
                  <i v-if="item.status.alerta" class="bx bx-error" aria-hidden="true"></i>
                  {{ textoPrazo(item) }}
                </div>
              </li>
            </transition-group>
            <p v-if="itensAtencao.length > itensVisiveis.length" class="jd-muted">
              E mais {{ itensAtencao.length - itensVisiveis.length }} — veja todas em Minhas Metas.
            </p>
          </template>
          <p v-if="p1.resumo.estourados" class="jd-muted">
            <i class="bx bx-info-circle" aria-hidden="true"></i>
            {{ p1.resumo.estourados }} {{ plural(p1.resumo.estourados, "adesão passou", "adesões passaram") }} de
            {{ regras.dias_apos_vencimento }} dias do vencimento e não {{ plural(p1.resumo.estourados, "conta", "contam") }} mais
            neste fechamento.
          </p>
        </section>

        <!-- 4. Final -->
        <section v-else key="final" class="jd-fase jd-final">
          <div class="jd-trofeu" aria-hidden="true"><i class="bx bx-trophy"></i></div>
          <h3 class="jd-hero-titulo">Jornada de hoje concluída!</h3>
          <p class="jd-lead">
            Você está no nível <strong>{{ nivelTexto }}</strong> no fechamento de {{ rotuloFechamento }}
            ({{ nomeProduto }}).
            <template v-if="p1.resumo.faltam > 0"> Faltam <strong>{{ p1.resumo.faltam }}</strong> para bater a meta — foco nas pendentes!</template>
            <template v-else> Meta batida — agora é garantir a gordura.</template>
          </p>
          <p v-if="sequenciaDias > 0" class="jd-streak">
            <i class="bx bxs-flame" aria-hidden="true"></i>
            {{ sequenciaDias }} {{ plural(sequenciaDias, "dia seguido", "dias seguidos") }} acompanhando
          </p>
          <button type="button" class="btn btn-default jd-cta" @click="verMinhasMetas">
            <i class="bx bx-target-lock mr-1" aria-hidden="true"></i> Ver Minhas Metas
          </button>
          <p class="jd-muted jd-aviso-desativar">
            A jornada aparece na 1ª abertura do dia. Você pode desativá-la a qualquer momento em Minhas Metas.
          </p>
        </section>
      </transition>
    </div>

    <template #modal-footer>
      <div class="jd-acoes">
        <button v-if="!ultimaFase" type="button" class="btn btn-link jd-pular" @click="pular">Pular</button>
        <span class="jd-espaco"></span>
        <button v-if="fase > 0 && !ultimaFase" type="button" class="btn btn-light" @click="voltar">
          <i class="bx bx-chevron-left" aria-hidden="true"></i> Voltar
        </button>
        <button type="button" class="btn btn-default" @click="proximo">
          <template v-if="ultimaFase">Fechar</template>
          <template v-else-if="fase === 0">Começar <i class="bx bx-chevron-right" aria-hidden="true"></i></template>
          <template v-else>Próximo <i class="bx bx-chevron-right" aria-hidden="true"></i></template>
        </button>
      </div>
    </template>
  </b-modal>
</template>

<style scoped>
.jd {
  --jd-m1: var(--st-brand);
  --jd-m2: var(--st-success-strong);
  --jd-m3: var(--st-warning-strong);
}
.jd-kicker {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--st-brand);
}
.jd-title {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--st-text);
  outline: none;
}
.jd-muted {
  color: var(--st-text-muted);
  font-size: 0.875rem;
}
.jd-lead {
  font-size: 0.9375rem;
  color: var(--st-text);
}

/* Trilha */
.jd-trilha {
  position: relative;
  margin: 0 0 1rem;
}
.jd-trilha-linha {
  position: absolute;
  top: 50%;
  left: 1rem;
  right: 1rem;
  height: 4px;
  margin-top: -2px;
  border-radius: 999px;
  background: var(--st-border-soft);
}
.jd-trilha-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--st-brand-gradient);
  transition: width 0.5s ease;
}
.jd-trilha-nos {
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  list-style: none;
}
.jd-trilha-nos li {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 2px solid var(--st-border);
  border-radius: 50%;
  background: var(--st-surface);
  color: var(--st-text-soft);
  font-size: 1rem;
  transition: transform 0.3s ease, background 0.3s ease;
}
.jd-trilha-nos li.feito {
  border-color: var(--st-success);
  background: var(--st-success);
  color: var(--st-text-inverse);
}
.jd-trilha-nos li.ativo {
  border-color: var(--st-brand);
  background: var(--st-brand);
  color: var(--st-text-inverse);
  transform: scale(1.15);
  box-shadow: 0 0 0 4px var(--st-brand-soft);
}

/* Abas de produto */
.jd-tabs {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  padding: 0.25rem;
  border-radius: 999px;
  background: var(--st-canvas);
}
.jd-tab {
  flex: 1;
  min-height: 2.5rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--st-text-muted);
  font-weight: 600;
}
.jd-tab.ativo {
  background: var(--st-brand);
  color: var(--st-text-inverse);
}

/* Abertura / final */
.jd-abertura,
.jd-final {
  text-align: center;
}
.jd-hero-icone,
.jd-trofeu {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 4.5rem;
  margin-bottom: 0.5rem;
  border-radius: 50%;
  background: var(--st-brand-gradient);
  color: var(--st-text-inverse);
  font-size: 2.25rem;
  box-shadow: var(--st-shadow-md);
  animation: jd-flutuar 3s ease-in-out infinite;
}
.jd-trofeu {
  background: linear-gradient(135deg, #f6d365, #d4a017);
}
.jd-saudacao {
  margin: 0;
  font-weight: 600;
  color: var(--st-text-muted);
}
.jd-hero-titulo {
  margin: 0.25rem 0 0.75rem;
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--st-text);
}
.jd-mapa {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
  margin: 1rem 0;
  padding: 0;
  list-style: none;
}
.jd-mapa li {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 0.25rem;
  border-radius: var(--st-radius-lg);
  background: var(--st-canvas);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--st-text);
  animation: jd-entrar 0.45s ease-out both;
}
.jd-mapa-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: var(--st-brand-soft);
  color: var(--st-brand);
  font-size: 1.125rem;
}
.jd-streak {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  background: var(--st-warning-soft);
  color: var(--st-warning-strong);
  font-weight: 700;
  font-size: 0.875rem;
}
.jd-cta {
  display: block;
  margin: 1rem auto 0.5rem;
  min-width: 14rem;
}
.jd-aviso-desativar {
  font-size: 0.8125rem;
}

/* KPIs */
.jd-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

/* Barra simples (fase 1) */
.jd-barra-simples {
  height: 0.875rem;
  border-radius: 999px;
  background: var(--st-canvas);
  border: 1px solid var(--st-border-soft);
  overflow: hidden;
}
.jd-barra-simples-fill {
  height: 100%;
  border-radius: 999px;
}
.jd-barra-simples-fill.andando { background: var(--st-warning); }
.jd-barra-simples-fill.ok { background: var(--st-success); }
.jd-legenda {
  margin: 0.375rem 0 1rem;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.jd-dica {
  display: flex;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border-radius: var(--st-radius);
  background: var(--st-brand-soft);
  font-size: 0.875rem;
}
.jd-dica > i {
  font-size: 1.125rem;
  color: var(--st-brand);
}

/* Fechamento */
.jd-fech-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 2.25rem;
}
.jd-nivel {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.875rem;
  background: var(--st-canvas);
  color: var(--st-text);
}
.jd-nivel-quase { background: var(--st-warning-soft); color: var(--st-warning-strong); }
.jd-nivel-batida,
.jd-nivel-gordura { background: var(--st-success-soft); color: var(--st-success-strong); }
.jd-nivel-superou { background: var(--st-brand-gradient); color: var(--st-text-inverse); }
.jd-barra-wrap {
  margin: 1rem 0 0.75rem;
  padding-top: 1.75rem;
}
.jd-barra-valor {
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  color: var(--st-text-muted);
  font-variant-numeric: tabular-nums;
}
.jd-barra-valor strong {
  font-size: 1.5rem;
  color: var(--st-text);
}
.jd-barra {
  position: relative;
  height: 1rem;
  border-radius: 999px;
  background: var(--st-canvas);
  border: 1px solid var(--st-border-soft);
}
.jd-barra-fill {
  height: 100%;
  border-radius: 999px;
}
.jd-fill-success { background: var(--st-success); }
.jd-fill-warning { background: var(--st-warning); }
.jd-fill-danger { background: var(--st-danger); }
.jd-marcas {
  position: absolute;
  inset: 0;
}
.jd-marca {
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 3px;
  margin-left: -1.5px;
  border-radius: 2px;
  background: var(--jd-cor, var(--st-text-soft));
}
.jd-marca-num {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 2px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  margin-left: -0.6875rem;
  border-radius: 50%;
  background: var(--jd-cor, var(--st-text-soft));
  color: var(--st-text-inverse);
  font-size: 0.75rem;
  font-weight: 700;
}
/* Número embaixo da barra (2ª marca sempre; 3ª quando colide com a 1ª). */
.jd-marca.baixo .jd-marca-num {
  top: calc(100% + 2px);
  bottom: auto;
}
.jd-m1 { --jd-cor: var(--jd-m1); }
.jd-m2 { --jd-cor: var(--jd-m2); }
.jd-m3 { --jd-cor: var(--jd-m3); }
.jd-marca.atual .jd-marca-num {
  animation: jd-pulsar 1.2s ease-in-out 2;
}
.jd-legenda-marcas {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  margin: 1.75rem 0 0;
  padding: 0;
  list-style: none;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.jd-legenda-marcas li.atual {
  color: var(--st-text);
}
.jd-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  margin-right: 0.125rem;
  border-radius: 50%;
  background: var(--jd-cor);
  color: var(--st-text-inverse);
  font-size: 0.6875rem;
  font-weight: 700;
}
.jd-explica {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 0.875rem;
  border-left: 4px solid var(--st-brand);
  border-radius: var(--st-radius);
  background: var(--st-brand-soft);
  font-size: 0.875rem;
  color: var(--st-text);
}

/* Contam */
.jd-lista {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  padding: 0;
  list-style: none;
}
.jd-item {
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--st-border-soft);
  border-left: 4px solid var(--st-warning);
  border-radius: var(--st-radius);
  background: var(--st-surface);
  font-size: 0.8125rem;
}
.jd-item.alerta {
  border-left-color: var(--st-danger);
  background: var(--st-warning-soft);
}
.jd-item.perdida {
  border-left-color: var(--st-border);
  opacity: 0.85;
}
.jd-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.jd-item-placa {
  font-size: 0.9375rem;
  letter-spacing: 0.03em;
}
.jd-item-nome {
  color: var(--st-text);
  font-weight: 600;
}
.jd-item-sub {
  color: var(--st-text-muted);
}
.jd-prazo-alerta {
  font-weight: 700;
  color: var(--st-danger-strong);
}
.jd-corte {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--st-radius);
  background: var(--st-brand-soft);
  font-size: 0.875rem;
  color: var(--st-text);
}
.jd-corte > i {
  font-size: 1.125rem;
  color: var(--st-brand);
}
.jd-corte.urgente {
  background: var(--st-warning-soft);
}
.jd-corte.urgente > i {
  color: var(--st-warning-strong);
}
.jd-vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 1.5rem 1rem;
  border-radius: var(--st-radius-lg);
  background: var(--st-success-soft);
  color: var(--st-success-strong);
  text-align: center;
}
.jd-vazio-icone {
  font-size: 2.5rem;
  animation: jd-flutuar 3s ease-in-out infinite;
}
.mm-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}
.mm-badge-ok { background: var(--st-status-approved-bg); color: var(--st-status-approved-text); }
.mm-badge-info { background: var(--st-status-returned-bg); color: var(--st-status-returned-text); }
.mm-badge-warn { background: var(--st-status-pending-bg); color: var(--st-status-pending-text); }
.mm-badge-bad { background: var(--st-status-rejected-bg); color: var(--st-status-rejected-text); }
.mm-badge-neutral { background: var(--st-canvas); color: var(--st-text-muted); }

/* Rodapé */
.jd-acoes {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin: 0;
}
.jd-espaco {
  flex: 1;
}
.jd-pular {
  color: var(--st-text-muted);
}

/* Transições */
.jd-fase-enter-active,
.jd-fase-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.jd-fase-enter {
  opacity: 0;
  transform: translateX(24px);
}
.jd-fase-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}
.jd-marca-enter-active,
.jd-pop-enter-active {
  transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.jd-marca-enter,
.jd-pop-enter {
  opacity: 0;
  transform: scale(0.4);
}
.jd-item-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.jd-item-enter {
  opacity: 0;
  transform: translateY(10px);
}
@keyframes jd-entrar {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
}
@keyframes jd-flutuar {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
@keyframes jd-pulsar {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.3); }
}
@media (prefers-reduced-motion: reduce) {
  .jd-fase-enter-active,
  .jd-fase-leave-active,
  .jd-marca-enter-active,
  .jd-pop-enter-active,
  .jd-item-enter-active,
  .jd-trilha-fill,
  .jd-trilha-nos li {
    transition: none;
  }
  .jd-mapa li,
  .jd-hero-icone,
  .jd-trofeu,
  .jd-vazio-icone,
  .jd-marca.atual .jd-marca-num {
    animation: none;
  }
}

@media (max-width: 767.98px) {
  .jd-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }
  .jd-lista {
    grid-template-columns: minmax(0, 1fr);
  }
  .jd-mapa {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

<style>
/* Não-scoped: o b-modal é teleportado para o body. Tela cheia no celular (webview do app). */
@media (max-width: 575.98px) {
  .jd-dialog {
    margin: 0;
    max-width: none;
    min-height: 100%;
  }
  .jd-dialog .jd-content {
    min-height: 100vh;
    min-height: 100dvh;
    border: 0;
    border-radius: 0;
  }
  .jd-dialog .jd-footer {
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
  }
}
</style>
